import React, { useEffect, useState } from "react";
import Avatar from "../Avatar/Avatar";
import useImage from "@/hooks/useImage";
import useLang from "@/hooks/useLang";
import { useAppDispatch } from "@/lib/store/hooks";
import { setToken } from "@/lib/store/features/token/tokenSlice";
import { useRouter } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { googleLogout } from "@react-oauth/google";
import { useAuthContext } from "@/contexts/AuthContext";
import { User2 } from "lucide-react";

interface AccountCardProps {
  image: string | undefined;
  username: string;
}

const AccountCard = ({ image, username }: AccountCardProps) => {
  const dispatch = useAppDispatch();
  const { logout, isAuthenticated } = useAuthContext();
  const router = useRouter();
  const { noImage } = useImage();
  const { getText } = useLang();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    googleLogout();
    router.replace("/auth/signin");
  };

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(setToken({ token: null, userId: 0 }));
    }
  }, [dispatch, isAuthenticated, logout]);

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col space-y-3 justify-between items-start">
        <p>{getText({ id: "text_connect" })}</p>
        <div className="flex space-x-3 justify-between items-start">
          <Button
            className="bg-secondary"
            onClick={() => router.replace("/auth/signin")}
          >
            <User2 />
            {getText({ id: "login" })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex space-x-3 justify-between items-center">
      <div className="flex space-x-4 items-center">
        {image !== undefined ? (
          <Avatar image={image} username={username} size={56} suggestion />
        ) : (
          <Avatar image={noImage} username={username} size={56} suggestion />
        )}
        <div className="flex flex-col space-y-1 overflow-hidden">
          <span className="font-semibold">{username}</span>
        </div>
      </div>
      <span
        className="text-secondary font-semibold cursor-pointer hover:text-primary"
        onClick={() => setOpen(true)}
      >
        {getText({ id: "switch" })}
      </span>

      <Dialog open={open} onOpenChange={() => setOpen(false)}>
        <DialogContent className="max-w-sm text-center items-center justify-center w-full">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center">
              {getText({ id: "logout" })}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>{getText({ id: "askLogout" })}</DialogDescription>
          <div className="flex items-center justify-center w-full">
            <Button className="bg-green-600 me-2 w-2/3" onClick={handleLogout}>
              {getText({ id: "alert.yes" })}
            </Button>
            <Button
              className=" bg-red-600 w-2/3"
              onClick={() => setOpen(false)}
            >
              {getText({ id: "alert.no" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountCard;
